import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { Clear as ClearIcon } from '@mui/icons-material';
import { IconButton, InputAdornment } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  getAttendanceSummaryCards,
  getPersons,
} from '../utils/api';
import { snapshotIsVendor, snapshotIsVisitor } from '../utils/employeeTypeUi';
import { buildPersonDetailsUrl } from '../utils/personDetailsNavigation';

const SEARCH_MIN_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;
const RESULT_LIMIT = 12;
const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;

const PERSON_TYPE_META = {
  employee: {
    label: 'Employee',
    detailKind: 'employee',
  },
  vendor: {
    label: 'Vendor',
    detailKind: 'vendor',
  },
  visitor: {
    label: 'Visitor',
    detailKind: 'visitor',
  },
};

/** Today's Present window (IST calendar day), matching AuditPresentView. */
const buildTodayPresenceRange = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const startDateIST = new Date(Date.UTC(y, m, d, 0, 0, 0));
  const endDateIST = new Date(Date.UTC(y, m, d, 23, 59, 59));
  return {
    start_time: new Date(startDateIST.getTime() - IST_OFFSET_MS).toISOString().slice(0, 16),
    end_time: new Date(endDateIST.getTime() - IST_OFFSET_MS).toISOString().slice(0, 16),
  };
};

const resolvePersonType = (person) => {
  if (snapshotIsVisitor(person?.employee_type)) return 'visitor';
  if (snapshotIsVendor(person?.employee_type)) return 'vendor';
  return 'employee';
};

const normalizePerson = (person) => {
  const pid = person?.person_id != null ? String(person.person_id).trim() : '';
  const name = person?.name != null ? String(person.name).trim() : '';
  if (!pid || !name) return null;

  const type = resolvePersonType(person);
  return {
    id: `${type}:${pid}`,
    type,
    person_id: pid,
    name,
    employee_code: person.employee_code || '',
    company_name: person.company_name || '',
    branch_name: person.branch_name || '',
    employee_type: person.employee_type || null,
    status: null,
  };
};

const getLatestStatus = (summary) => {
  const recentEvents = Array.isArray(summary?.recent_events) ? summary.recent_events : [];
  const latestEvent = recentEvents[0];
  const action = String(latestEvent?.action || '').trim().toLowerCase();
  if (action === 'check_in' || action === 'checkin') return 'In';
  if (action === 'check_out' || action === 'checkout') return 'Out';
  return null;
};

const fetchStatusSafely = async (person) => {
  try {
    const { start_time, end_time } = buildTodayPresenceRange();
    const data = await getAttendanceSummaryCards({
      person_id: person.person_id,
      start_time,
      end_time,
      days: null,
      limit: 1,
      offset: 0,
      sort_order: 'desc',
      is_intrusion: false,
    });
    const summary = Array.isArray(data?.summaries) ? data.summaries[0] : null;
    return getLatestStatus(summary);
  } catch (error) {
    console.error('Global search status lookup failed', error);
    return null;
  }
};

const searchPeople = async (query) => {
  let persons = [];
  try {
    // Omit is_vendor / is_visitor so employees, vendors, and visitors are all returned.
    const data = await getPersons(
      RESULT_LIMIT,
      0,
      false,
      query,
      null,
      null,
      null,
      false,
      null,
      null,
      null,
      null
    );
    persons = Array.isArray(data?.persons) ? data.persons : [];
  } catch (error) {
    if (error?.status !== 403) {
      console.error('Global people search failed', error);
    }
    return [];
  }

  const seen = new Set();
  const merged = persons
    .map(normalizePerson)
    .filter(Boolean)
    .filter((person) => {
      if (seen.has(person.person_id)) return false;
      seen.add(person.person_id);
      return true;
    });

  const statuses = await Promise.all(merged.map(fetchStatusSafely));
  return merged.map((person, index) => ({
    ...person,
    status: statuses[index] || null,
  }));
};

const GlobalPeopleSearch = ({ sx = {} }) => {
  const navigate = useNavigate();
  const requestIdRef = useRef(0);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const trimmedInput = useMemo(() => inputValue.trim(), [inputValue]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    if (trimmedInput.length < SEARCH_MIN_LENGTH) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const results = await searchPeople(trimmedInput);
        if (requestId === requestIdRef.current) {
          setOptions(results);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [trimmedInput]);

  const handleSelect = (event, person) => {
    if (!person) return;
    const meta = PERSON_TYPE_META[person.type] || PERSON_TYPE_META.employee;
    const url = buildPersonDetailsUrl(person.person_id, { detailKind: meta.detailKind });
    if (url) {
      setInputValue('');
      setOptions([]);
      navigate(url);
    }
  };

  return (
    <Autocomplete
      size="small"
      clearOnBlur={false}
      filterOptions={(items) => items}
      getOptionLabel={(option) => option?.name || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={loading}
      forcePopupIcon={false}
      noOptionsText={
        trimmedInput.length < SEARCH_MIN_LENGTH
          ? 'Type at least 2 characters'
          : 'No active people found'
      }
      options={options}
      value={null}
      inputValue={inputValue}
      onChange={handleSelect}
      onInputChange={(event, newValue, reason) => {
        if (reason === 'reset') return;
        setInputValue(newValue);
      }}
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', sm: 420, lg: 480 },
        minWidth: { xs: '100%', md: 320 },
        flexShrink: 0,
        mb: 2,
        ...sx,
      }}
      slotProps={{
        paper: {
          elevation: 6,
          sx: {
            borderRadius: 1,
            mt: 0.5,
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search people"
          aria-label="Search active employees, visitors, and vendors"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20, mr: 1 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {/* {loading ? <CircularProgress color="inherit" size={18} /> : null} */}
                {inputValue ? (
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={(e) => {
                        e.stopPropagation();
                        setInputValue('');
                        setOptions([]);
                      }}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const meta = PERSON_TYPE_META[option.type] || PERSON_TYPE_META.employee;

        return (
          <Box
            component="li"
            {...props}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              minHeight: 56,
              width: '100%',
              boxSizing: 'border-box',
              '&.MuiAutocomplete-option': {
                alignItems: 'center',
              },
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1, pr: 1.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {option.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {meta.label}
              </Typography>
            </Box>
            {option.status ? (
              <Chip
                size="small"
                label={option.status}
                color={option.status === 'In' ? 'success' : 'default'}
                sx={{
                  flexShrink: 0,
                  height: 22,
                  ml: 1,
                  ...(option.status === 'Out'
                    ? {
                        bgcolor: '#FEE2E2',
                        color: '#991B1B',
                        '& .MuiChip-label': { color: '#991B1B' },
                      }
                    : null),
                }}
              />
            ) : null}
          </Box>
        );
      }}
    />
  );
};

export default GlobalPeopleSearch;
